import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
const { connect } = require('react-redux');
import { SearchInput } from 'components';
import renderIcon from 'helpers/renderIcon';
import { IShopCardAction, IShopCardData } from 'models/shop/shopcard';
import { IUserCardAction, IUserCardData } from 'models/user/usercard';
import { IStore } from 'redux/IStore';
import {
	getUserFollowers,
	getUserFollowing,
	setKeywordFilter as setKeywordFilterUser,
} from 'redux/modules/user/usercard';
import {
	filterShopCustomers,
	getShopCustomers,
	getShopFollowers,
	setKeywordFilter as setKeywordFilterShop,
} from 'redux/modules/shop/shopcard';

interface IState {
	searchOpen: boolean;
	hasSearched: boolean;
}

interface IProps {
	parentCard: 'user' | 'shop';
	userType: 'followers' | 'following' | 'customer';
	shopCardData?: IShopCardData;
	userCardData?: IUserCardData;

	/* function props */
	t?: (key: string, options?: any) => string;
	setKeywordFilterShop?: (keywordFilter: string) => IShopCardAction;
	setKeywordFilterUser?: (keywordFilter: string) => IUserCardAction;
	getShopFollowers?: (userId: string, isFirstLoad: boolean, keyword: string) => IShopCardAction;
	getShopCustomers?: (shopId: string, currentPage: number, clean?: boolean) => IShopCardAction;
	getUserFollowers?: (userId: string, isFirstLoad: boolean, keyword: string) => IUserCardAction;
	getUserFollowing?: (userId: string, isFirstLoad: boolean, keyword: string) => IUserCardAction;
	filterShopCustomers?: (keyword: string) => IShopCardAction;
}

@connect(
	(state: IStore) => ({
		shopCardData: state.shopcard,
		userCardData: state.usercard,
	}),
	(dispatch) => ({
		setKeywordFilterShop: (keywordFilter: string) => dispatch(setKeywordFilterShop(keywordFilter)),
		setKeywordFilterUser: (keywordFilter: string) => dispatch(setKeywordFilterUser(keywordFilter)),
		getShopFollowers: (id: string, isFirstLoad: boolean, keyword: string) =>
			dispatch(getShopFollowers(id, isFirstLoad, keyword)),
		getShopCustomers: (shopId: string, currentPage: number, clean?: boolean) =>
			dispatch(getShopCustomers(shopId, currentPage, clean)),
		getUserFollowers: (id: string, isFirstLoad: boolean, keyword: string) =>
			dispatch(getUserFollowers(id, isFirstLoad, keyword)),
		getUserFollowing: (id: string, isFirstLoad: boolean, keyword: string) =>
			dispatch(getUserFollowing(id, isFirstLoad, keyword)),
		filterShopCustomers: (keyword: string) => dispatch(filterShopCustomers(keyword)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class SearchUsers extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);
		this.handleSearchToggle = this.handleSearchToggle.bind(this);
		this.handleSearchEnterPress = this.handleSearchEnterPress.bind(this);
		this.handleSearchValueChange = this.handleSearchValueChange.bind(this);
		this.handleClearSearchInput = this.handleClearSearchInput.bind(this);
		this.getUsers = this.getUsers.bind(this);
		this.state = {
			searchOpen: false,
			hasSearched: false,
		};
	}
	customRef = React.createRef<HTMLInputElement>();

	componentDidUpdate(_prevProps: Readonly<IProps>, prevState: Readonly<IState>): void {
		// focus input when search opens
		if (prevState.searchOpen !== this.state.searchOpen && this.state.searchOpen && this.customRef.current) {
			this.customRef.current.focus();
		}
		// reset data when the user already searched for something and then deleted everything in the input field
		if (
			(this.state.hasSearched &&
				_prevProps.shopCardData.keywordFilter?.length > 0 &&
				this.props.shopCardData.keywordFilter?.length === 0) ||
			(this.state.hasSearched &&
				_prevProps.userCardData.keywordFilter?.length > 0 &&
				this.props.userCardData.keywordFilter?.length === 0)
		) {
			this.getUsers();
			this.setState({ hasSearched: false });
		}
	}

	private getUsers() {
		if (this.props.parentCard === 'shop') {
			if (this.props.userType === 'followers') {
				this.props.getShopFollowers(this.props.shopCardData.shopcard.id, true, this.props.shopCardData.keywordFilter);
			}
			if (this.props.userType === 'customer') {
				this.props.filterShopCustomers(this.props.shopCardData.keywordFilter);
			}
		}
		if (this.props.parentCard === 'user') {
			if (this.props.userType === 'followers') {
				this.props.getUserFollowers(this.props.userCardData.usercard.id, true, this.props.userCardData.keywordFilter);
			}
			if (this.props.userType === 'following') {
				this.props.getUserFollowing(this.props.userCardData.usercard.id, true, this.props.userCardData.keywordFilter);
			}
		}
	}
	private handleSearchToggle() {
		this.setState({ searchOpen: !this.state.searchOpen });
	}

	private handleSearchEnterPress(e: React.KeyboardEvent) {
		if (e.key === 'Enter') {
			this.getUsers();
			this.setState({ searchOpen: false, hasSearched: true });
		}
	}

	public handleSearchValueChange(e: React.KeyboardEvent) {
		const value: string = (e.target as HTMLInputElement).value;
		// Value length is undefined if we deleted everything in the field
		if (this.props.parentCard === 'shop') {
			this.props.setKeywordFilterShop(value?.length > 0 ? value : '');
		} else if (this.props.parentCard === 'user') {
			this.props.setKeywordFilterUser(value?.length > 0 ? value : '');
		}
	}

	// PortalInputField first triggers its own clear function when clicking on close which cleans the text
	// The second click triggers the function below which closes the input field
	private handleClearSearchInput() {
		if (this.props.parentCard === 'shop') {
			this.props.setKeywordFilterShop('');
		} else if (this.props.parentCard === 'user') {
			this.props.setKeywordFilterUser('');
		}
		this.handleSearchToggle();
	}

	render() {
		const { t, parentCard, userType, shopCardData } = this.props;
		let totalUsers: number;
		let userTypeName: string;
		let keywordFilter: string;
		const searchPlaceholder = t('PortalPage_Section_Manage_Common_SearchUser');

		if (parentCard === 'shop') {
			if (userType === 'followers' || userType === 'following') {
				totalUsers = shopCardData.followersPage.totalItems || 0;
			}
			if (userType === 'customer') {
				totalUsers = shopCardData.customers?.totalItems || 0;
			}
			keywordFilter = shopCardData.keywordFilter;
		}

		if (parentCard === 'user') {
			if (userType === 'followers') {
				totalUsers = this.props.userCardData.followersPage.totalItems || 0;
			}
			if (userType === 'following') {
				totalUsers = this.props.userCardData.followingPage.totalItems || 0;
			}
			keywordFilter = this.props.userCardData.keywordFilter;
		}

		if (userType === 'followers') {
			userTypeName = t('BSPortal_Common_Followers');
		}
		if (userType === 'following') {
			userTypeName = t('BSPortal_Common_Following');
		}
		if (userType === 'customer') {
			userTypeName = t('User_ShopCard_Customers');
		}

		return (
			<div className="row searchUsers d-flex align-items-center">
				<div className="col-md-6 d-flex align-items-center">
					<p className="p12 follow-count">
						{totalUsers} {userTypeName}
					</p>
				</div>
				<div className="col-md-6 d-flex justify-content-end">
					{!this.state.searchOpen ? (
						renderIcon('search3', 40, 40, 'searchIcon pointer', this.handleSearchToggle)
					) : (
						<SearchInput
							keyword={keywordFilter}
							onSearchEnterPress={this.handleSearchEnterPress}
							onSearchValueChange={this.handleSearchValueChange}
							onClearSearchInput={this.handleClearSearchInput}
							customRef={this.customRef}
							placeholder={searchPlaceholder}
							customIconSizeHeight={32}
							customIconSizeWidth={32}
							doesNotNeedUrlChange={true}
						/>
					)}
				</div>
			</div>
		);
	}
}

export default SearchUsers;
